import { useState, useEffect } from 'react';
import { useAuth } from '../components/Auth/AuthContext';
import userInteractionService from '../services/userInteractionService';

export function useUserInteractions() {
  const { token, isAuthenticated } = useAuth();
  const [userLikes, setUserLikes] = useState(new Set());
  const [userVisits, setUserVisits] = useState(new Map()); // Map of spotId -> visit count
  const [loading, setLoading] = useState(false);

  // Load user interactions on auth state change
  useEffect(() => {
    if (isAuthenticated && token) {
      loadUserInteractions();
    } else {
      // Clear interactions when user logs out
      setUserLikes(new Set());
      setUserVisits(new Map());
    }
  }, [isAuthenticated, token]);

  const loadUserInteractions = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const [likedSpots, visitedSpots] = await Promise.all([
        userInteractionService.getLikedSpots(token),
        userInteractionService.getVisitedSpots(token)
      ]);

      // Create Set of liked spot IDs
      const likedSpotIds = new Set(likedSpots.map(spot => spot.id));
      setUserLikes(likedSpotIds);

      // Create Map of visited spot IDs -> visit count
      const visitMap = new Map();
      visitedSpots.forEach(spot => {
        visitMap.set(spot.id, spot.visit_history?.length || 0);
      });
      setUserVisits(visitMap);

    } catch (error) {
      console.error('Error loading user interactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async (spotId) => {
    if (!isAuthenticated || !token) {
      throw new Error('User must be authenticated to like spots');
    }

    // Optimistic update - toggle immediately
    const wasLiked = userLikes.has(spotId);
    const newLikes = new Set(userLikes);
    if (wasLiked) {
      newLikes.delete(spotId);
    } else {
      newLikes.add(spotId);
    }
    setUserLikes(newLikes);

    try {
      const result = await userInteractionService.likeSpot(spotId, token);
      return result;
    } catch (error) {
      // Revert on error
      const revertedLikes = new Set(userLikes);
      if (wasLiked) {
        revertedLikes.add(spotId);
      } else {
        revertedLikes.delete(spotId);
      }
      setUserLikes(revertedLikes);
      console.error('Error toggling like:', error);
      throw error;
    }
  };

  const addVisit = async (spotId, visitData) => {
    if (!isAuthenticated || !token) {
      throw new Error('User must be authenticated to add visits');
    }

    try {
      const result = await userInteractionService.addVisit(spotId, visitData, token);
      
      // Update local state
      const newVisits = new Map(userVisits);
      const currentCount = newVisits.get(spotId) || 0;
      newVisits.set(spotId, currentCount + 1);
      setUserVisits(newVisits);

      return result;
    } catch (error) {
      console.error('Error adding visit:', error);
      throw error;
    }
  };

  const likePhoto = async (photoUrl, spotId) => {
    if (!isAuthenticated || !token) {
      throw new Error('User must be authenticated to like photos');
    }

    try {
      const result = await userInteractionService.likePhoto(photoUrl, spotId, token);
      return result;
    } catch (error) {
      console.error('Error liking photo:', error);
      throw error;
    }
  };

  // Helper functions
  const isSpotLiked = (spotId) => userLikes.has(spotId);
  const isSpotVisited = (spotId) => userVisits.has(spotId) && userVisits.get(spotId) > 0;
  const getVisitCount = (spotId) => userVisits.get(spotId) || 0;

  return {
    userLikes,
    userVisits,
    loading,
    isSpotLiked,
    isSpotVisited,
    getVisitCount,
    toggleLike,
    addVisit,
    likePhoto,
    loadUserInteractions
  };
}

export default useUserInteractions;